import React, { useState } from 'react';
import { Button, Flex, Gap, Icon, Text } from '@common/components';
import { Colors } from '@common/tokens';
import { highlightSegments } from './_schedule-ai-summary-highlight';

type Props = {
  summary?: {
    whatBalancerDid: string;
    callouts?: string | null;
  } | null;
  // The run's configured clinical role abbreviations, loaded from the backend,
  // so role mentions in the summary bold against the actual service-line config
  // rather than a hardcoded list.
  roles?: string[];
  // Stable per-(run, unit, generation) key. The collapsed/dismissed choice is
  // persisted under it in localStorage so it survives reloads; because the key
  // includes the unit and generatedAt, switching unit or regenerating the summary
  // yields a fresh key and the card returns to its default (expanded, visible).
  storageKey: string;
};

type CardState = { collapsed: boolean; dismissed: boolean };

const DEFAULT_CARD_STATE: CardState = { collapsed: false, dismissed: false };

// localStorage is best-effort here: private mode, disabled storage, or a quota
// error must never break the card, so a read falls back to the default and a
// write is swallowed.
function readCardState(key: string): CardState {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return DEFAULT_CARD_STATE;
    const parsed = JSON.parse(raw) as Partial<CardState>;
    return { collapsed: !!parsed.collapsed, dismissed: !!parsed.dismissed };
  } catch {
    return DEFAULT_CARD_STATE;
  }
}

function writeCardState(key: string, state: CardState) {
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Persistence is a nicety, not a requirement — ignore storage failures.
  }
}

// Renders one body paragraph with salient keywords/numbers/roles bolded. Each
// segment is its own inline Text so bold runs use the design-system weight class
// (robust against the global `strong` reset that would otherwise flatten browser
// bold).
function renderBody(text: string, roles: string[]) {
  return highlightSegments(text, roles).map((segment, index) => (
    <Text key={`${index}-${segment.text}`} size="sm" bold={segment.bold}>
      {segment.text}
    </Text>
  ));
}

/**
 * Manager-facing AI summary of the balancer's moves for the reviewed unit, shown
 * in the review side panel. Split into a brief "What the balancer did" recap and
 * a longer, precise "Callouts for your review" section. Generated asynchronously
 * at pipeline completion, so it is absent until ready — this renders nothing when
 * there is no recap. The manager can dismiss the card.
 *
 * The sparkle icon is filled with an iridescent gradient (an inline SVG gradient
 * def + `fill: url(...)`) and slowly shimmers through the spectrum via an animated
 * hue rotation (disabled under prefers-reduced-motion).
 */
export function ScheduleAiSummary({ summary, roles = [], storageKey }: Props) {
  // Initialised from localStorage; the parent remounts this component when
  // storageKey changes (unit switch / regeneration), so the initializer re-reads
  // the right entry rather than leaking one unit's choice onto the next.
  const [cardState, setCardState] = useState<CardState>(() =>
    readCardState(storageKey)
  );
  const { collapsed, dismissed } = cardState;

  const persist = (patch: Partial<CardState>) =>
    setCardState((prev) => {
      const next = { ...prev, ...patch };
      writeCardState(storageKey, next);
      return next;
    });

  if (!summary?.whatBalancerDid || dismissed) {
    return null;
  }

  return (
    <div className="schedule-ai-summary">
      {/* Gradient definition for the iridescent sparkle fill. */}
      <svg
        className="schedule-ai-summary__defs"
        width="0"
        height="0"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient id="wk-ai-iridescent" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#7c3aed" />
            <stop offset="35%" stopColor="#ec4899" />
            <stop offset="70%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#34d399" />
          </linearGradient>
        </defs>
      </svg>

      <Flex align="center" justify="space-between">
        <Gap size="xs" align="center">
          <span className="schedule-ai-summary__sparkle">
            <Icon icon="sparkles" kind="light" size="md" />
          </span>
          <Text bold size="md">
            AI Summary
          </Text>
        </Gap>
        <Gap size="xs" align="center">
          <Button
            kind="link"
            size="sm"
            shape="circle"
            icon={
              <Icon
                icon={collapsed ? 'chevronDown' : 'chevronUp'}
                kind="light"
                size="sm"
              />
            }
            ariaLabel={collapsed ? 'Expand AI summary' : 'Collapse AI summary'}
            aria-expanded={!collapsed}
            onClick={() => persist({ collapsed: !collapsed })}
          />
          <Button
            kind="link"
            size="sm"
            shape="circle"
            icon={<Icon icon="xMark" kind="light" size="sm" />}
            ariaLabel="Dismiss AI summary"
            onClick={() => persist({ dismissed: true })}
          />
        </Gap>
      </Flex>

      {!collapsed ? (
        <>
          <div className="schedule-ai-summary__section">
            <Text size="sm" secondary transform="uppercase">
              What the balancer did
            </Text>
            <div className="schedule-ai-summary__body">
              {renderBody(summary.whatBalancerDid, roles)}
            </div>
          </div>

          {summary.callouts ? (
            <div className="schedule-ai-summary__section">
              <Text size="sm" secondary transform="uppercase">
                Callouts for your review
              </Text>
              <div className="schedule-ai-summary__body">
                {renderBody(summary.callouts, roles)}
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      <style jsx>{`
        .schedule-ai-summary {
          border: 1px solid ${Colors.steel};
          border-radius: 8px;
          padding: 14px 16px;
          background-color: ${Colors.white};
        }
        .schedule-ai-summary__defs {
          position: absolute;
          width: 0;
          height: 0;
        }
        .schedule-ai-summary__section {
          margin-top: 12px;
        }
        .schedule-ai-summary__body {
          margin-top: 4px;
          /* Preserve the model's paragraph breaks. */
          white-space: pre-line;
          line-height: 1.5;
        }
        /* Reach the FontAwesome SVG (rendered by the Icon child) to gradient-fill
           and shimmer it. CSS fill overrides the glyph's currentColor attribute. */
        .schedule-ai-summary__sparkle {
          display: inline-flex;
          /* Fallback tint if a renderer ignores the gradient fill below. */
          color: #7c3aed;
          animation: wk-ai-shimmer 8s linear infinite;
        }
        .schedule-ai-summary__sparkle :global(svg),
        .schedule-ai-summary__sparkle :global(svg path) {
          fill: url(#wk-ai-iridescent);
        }
        @keyframes wk-ai-shimmer {
          from {
            filter: hue-rotate(0deg);
          }
          to {
            filter: hue-rotate(360deg);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .schedule-ai-summary__sparkle {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
